import { useQuery } from '@tanstack/react-query'
import { Currency, CurrencyAmount } from '@uniswap/sdk-core'
import { useMemo } from 'react'
import { useUniswapContextSelector } from 'uniswap/src/contexts/UniswapContext'
import { useCheckApprovalQuery } from 'uniswap/src/data/apiClients/tradingApi/useCheckApprovalQuery'
import { ApprovalRequest, Routing } from 'uniswap/src/data/tradingApi/__generated__/index'
import { AccountMeta } from 'uniswap/src/features/accounts/types'
import { UniverseChainId } from 'uniswap/src/features/chains/types'
import {
  convertGasFeeToDisplayValue,
  useActiveGasStrategy,
  useShadowGasStrategies,
} from 'uniswap/src/features/gas/hooks'
import { GasFeeResult, areEqualGasStrategies } from 'uniswap/src/features/gas/types'
import { useTransactionSettingsContext } from 'uniswap/src/features/transactions/components/settings/contexts/TransactionSettingsContext'
import { getLocalFewV2ApprovalInfo } from 'uniswap/src/features/transactions/swap/hooks/localFewV2Approval'
import { ApprovalAction, TokenApprovalInfo } from 'uniswap/src/features/transactions/swap/types/trade'
import { isUniswapX } from 'uniswap/src/features/transactions/swap/utils/routing'
import {
  getTokenAddressForApi,
  toTradingApiSupportedChainId,
} from 'uniswap/src/features/transactions/swap/utils/tradingApi'
import { GasFeeEstimates } from 'uniswap/src/features/transactions/types/transactionDetails'
import { WrapType } from 'uniswap/src/features/transactions/types/wrap'
import { logger } from 'utilities/src/logger/logger'
import { ONE_MINUTE_MS, ONE_SECOND_MS } from 'utilities/src/time/time'

export interface TokenApprovalInfoParams {
  chainId: UniverseChainId
  wrapType: WrapType
  currencyInAmount: Maybe<CurrencyAmount<Currency>>
  currencyOutAmount?: Maybe<CurrencyAmount<Currency>>
  routing: Routing | undefined
  account?: AccountMeta
  /** When set (e.g. AGGREGATOR with methodParameters.to), passed to check_approval so backend can return approval for this spender or null if already approved */
  approvalSpender?: string
}

export type ApprovalTxInfo = {
  tokenApprovalInfo: TokenApprovalInfo
  approvalGasFeeResult: GasFeeResult
  revokeGasFeeResult: GasFeeResult
}

function useUnavailableProvider(): undefined {
  return undefined
}

function useApprovalWillBeBatchedWithSwap(chainId: UniverseChainId, routing: Routing | undefined): boolean {
  const canBatchTransactions = useUniswapContextSelector((ctx) => ctx.getCanBatchTransactions?.(chainId))
  const swapDelegationInfo = useUniswapContextSelector((ctx) => ctx.getSwapDelegationInfo?.(chainId))

  const isBatchableFlow = Boolean(routing && !isUniswapX({ routing }))

  return Boolean((canBatchTransactions || swapDelegationInfo?.delegationAddress) && isBatchableFlow)
}

export function useTokenApprovalInfo(params: TokenApprovalInfoParams): ApprovalTxInfo {
  const { account, chainId, wrapType, currencyInAmount, currencyOutAmount, routing, approvalSpender } = params

  const isWrap = wrapType === WrapType.Wrap || wrapType === WrapType.Unwrap

  const address = account?.address
  const inputWillBeWrapped = routing && isUniswapX({ routing })
  // Off-chain orders must have wrapped currencies approved, rather than natives.
  const currencyIn = inputWillBeWrapped ? currencyInAmount?.currency.wrapped : currencyInAmount?.currency
  const amount = currencyInAmount?.quotient.toString()

  const tokenInAddress = getTokenAddressForApi(currencyIn)
  const { requiredPoolAddress } = useTransactionSettingsContext()
  const useProviderHook = useUniswapContextSelector((ctx) => ctx.useProviderHook) ?? useUnavailableProvider
  const provider = useProviderHook(chainId)

  // Only used for bridging
  const isBridge = routing === Routing.BRIDGE
  const currencyOut = currencyOutAmount?.currency
  const tokenOutAddress = getTokenAddressForApi(currencyOut)

  const activeGasStrategy = useActiveGasStrategy(chainId, 'general')
  const shadowGasStrategies = useShadowGasStrategies(chainId, 'general')

  const approvalRequestArgs: ApprovalRequest | undefined = useMemo(() => {
    const tokenInChainId = toTradingApiSupportedChainId(chainId)
    const tokenOutChainId = toTradingApiSupportedChainId(currencyOut?.chainId)

    if (!address || !amount || !currencyIn || !tokenInAddress || !tokenInChainId) {
      return undefined
    }
    if (isBridge && !tokenOutAddress && !tokenOutChainId) {
      return undefined
    }

    return {
      walletAddress: address,
      token: tokenInAddress,
      amount,
      chainId: tokenInChainId,
      includeGasInfo: true,
      tokenOut: tokenOutAddress,
      tokenOutChainId,
      gasStrategies: [activeGasStrategy, ...(shadowGasStrategies ?? [])],
      ...(approvalSpender != null && approvalSpender !== '' ? { spender: approvalSpender } : {}),
    }
  }, [
    activeGasStrategy,
    address,
    amount,
    approvalSpender,
    chainId,
    currencyIn,
    currencyOut?.chainId,
    isBridge,
    tokenInAddress,
    tokenOutAddress,
    shadowGasStrategies,
  ])

  const approvalWillBeBatchedWithSwap = useApprovalWillBeBatchedWithSwap(chainId, routing)
  const useLocalApproval = Boolean(requiredPoolAddress)
  const localApprovalEnabled = Boolean(
    useLocalApproval && address && amount && currencyIn?.isToken && provider && currencyInAmount,
  )
  const localApprovalQuery = useQuery({
    queryKey: ['localFewV2TokenApproval', chainId, address, tokenInAddress, amount],
    queryFn: async () => {
      if (!address || !currencyIn?.isToken || !provider || !currencyInAmount) {
        return undefined
      }
      return getLocalFewV2ApprovalInfo({
        amount: CurrencyAmount.fromRawAmount(currencyIn, currencyInAmount.quotient),
        owner: address,
        provider,
      })
    },
    enabled: localApprovalEnabled,
    refetchInterval: 4 * ONE_SECOND_MS,
    staleTime: 4 * ONE_SECOND_MS,
  })
  const shouldSkip = !approvalRequestArgs || isWrap || !address || approvalWillBeBatchedWithSwap || useLocalApproval

  const { data, isLoading, error } = useCheckApprovalQuery({
    params: shouldSkip ? undefined : approvalRequestArgs,
    staleTime: 15 * ONE_SECOND_MS,
    immediateGcTime: ONE_MINUTE_MS,
  })

  const localApprovalResult = useMemo<ApprovalTxInfo | undefined>(() => {
    if (!useLocalApproval) {
      return undefined
    }

    if (isWrap || !address || approvalWillBeBatchedWithSwap) {
      return {
        tokenApprovalInfo: { action: ApprovalAction.None, txRequest: null, cancelTxRequest: null },
        approvalGasFeeResult: { value: '0', displayValue: '0', isLoading: false, error: null },
        revokeGasFeeResult: { value: '0', displayValue: '0', isLoading: false, error: null },
      }
    }

    const queryError = localApprovalQuery.error
    const queryIsLoading = localApprovalEnabled && localApprovalQuery.isLoading
    const approvalInfo = localApprovalQuery.data
    if (!approvalInfo) {
      const approvalError = !queryIsLoading
        ? queryError ?? new Error('Unable to read the local FewV2 token approval')
        : null
      return {
        tokenApprovalInfo: { action: ApprovalAction.Unknown, txRequest: null, cancelTxRequest: null },
        approvalGasFeeResult: { isLoading: queryIsLoading, error: approvalError },
        revokeGasFeeResult: { isLoading: queryIsLoading, error: approvalError },
      }
    }

    return {
      tokenApprovalInfo: approvalInfo.tokenApprovalInfo,
      approvalGasFeeResult: {
        value: approvalInfo.approvalGasFee,
        displayValue: convertGasFeeToDisplayValue(approvalInfo.approvalGasFee, activeGasStrategy),
        isLoading: false,
        error: null,
      },
      revokeGasFeeResult: {
        value: approvalInfo.revokeGasFee,
        displayValue: convertGasFeeToDisplayValue(approvalInfo.revokeGasFee, activeGasStrategy),
        isLoading: false,
        error: null,
      },
    }
  }, [
    activeGasStrategy,
    address,
    approvalWillBeBatchedWithSwap,
    isWrap,
    localApprovalEnabled,
    localApprovalQuery.data,
    localApprovalQuery.error,
    localApprovalQuery.isLoading,
    useLocalApproval,
  ])

  const tokenApprovalInfo: TokenApprovalInfo = useMemo(() => {
    if (error) {
      logger.error(error, {
        tags: { file: 'useTokenApprovalInfo', function: 'useTokenApprovalInfo' },
        extra: {
          approvalRequestArgs,
        },
      })
    }

    // Approval is N/A for wrap transactions or unconnected state.
    if (isWrap || !address || approvalWillBeBatchedWithSwap) {
      return {
        action: ApprovalAction.None,
        txRequest: null,
        cancelTxRequest: null,
      }
    }

    if (data && !error) {
      // API returns null if no approval is required
      if (data.approval === null) {
        return {
          action: ApprovalAction.None,
          txRequest: null,
          cancelTxRequest: null,
        }
      }

      if (data.approval) {
        if (data.cancel) {
          return {
            action: ApprovalAction.RevokeAndPermit2Approve,
            txRequest: data.approval,
            cancelTxRequest: data.cancel,
          }
        }

        return {
          action: ApprovalAction.Permit2Approve,
          txRequest: data.approval,
          cancelTxRequest: null,
        }
      }
    }

    // No valid approval type found
    return {
      action: ApprovalAction.Unknown,
      txRequest: null,
      cancelTxRequest: null,
    }
  }, [address, approvalRequestArgs, approvalWillBeBatchedWithSwap, data, error, isWrap])

  const apiApprovalResult = useMemo(() => {
    const activeEstimate = data?.gasEstimates?.find((e) => areEqualGasStrategies(e.strategy, activeGasStrategy))

    const noApprovalNeeded = tokenApprovalInfo.action === ApprovalAction.None
    const noRevokeNeeded =
      tokenApprovalInfo.action === ApprovalAction.Permit2Approve || tokenApprovalInfo.action === ApprovalAction.None
    const approvalFee = noApprovalNeeded ? '0' : data?.gasFee
    const revokeFee = noRevokeNeeded ? '0' : data?.cancelGasFee ?? '0'

    const unknownApproval = tokenApprovalInfo.action === ApprovalAction.Unknown
    const isGasLoading = unknownApproval && isLoading
    const approvalGasError = unknownApproval && !isLoading ? new Error('Approval action unknown') : null

    let gasEstimates: GasFeeEstimates | undefined
    if (activeEstimate) {
      gasEstimates = {
        activeEstimate,
        shadowEstimates: data?.gasEstimates?.filter((e) => e !== activeEstimate),
      }
    }

    return {
      tokenApprovalInfo,
      approvalGasFeeResult: {
        value: approvalFee,
        displayValue: convertGasFeeToDisplayValue(approvalFee, activeGasStrategy),
        isLoading: isGasLoading,
        error: approvalGasError,
        gasEstimates,
        activeEstimate,
      },
      revokeGasFeeResult: {
        value: revokeFee,
        displayValue: convertGasFeeToDisplayValue(revokeFee, activeGasStrategy),
        isLoading: isGasLoading,
        error: approvalGasError,
      },
    }
  }, [activeGasStrategy, data?.cancelGasFee, data?.gasEstimates, data?.gasFee, isLoading, tokenApprovalInfo])

  return localApprovalResult ?? apiApprovalResult
}
